﻿import { SbbSecondaryButton } from "@sbb-esta/lyne-react/button";
import { SbbFileSelectorDropzone } from "@sbb-esta/lyne-react/file-selector/file-selector-dropzone";
import { SbbFormField } from "@sbb-esta/lyne-react/form-field";
import classNames from "classnames";
import { LeanLabel } from "components/Lean/LeanLabel";
import { FileActionEnum } from "enums/FileActionEnum";
import { getTruncatedText } from "helpers";
import { createLogger } from "logging";
import { useEnumLabels } from "providers";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { DocumentEditModel } from "./DocumentEditModel";
import { HookFormError } from "./HookFormError";

const logger = createLogger("HookFormDocuments");

interface HookFormDocumentsProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> {
  label: string;
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
  required?: boolean;
  multiple?: boolean;
  accept?: string;
}

let lastValue: unknown | null = null;
let files = Array<DocumentEditModel>();

function getFiles(multiple: boolean, value: unknown): DocumentEditModel[] {
  if (value === lastValue) {
    return files;
  }

  files = multiple ? (value as DocumentEditModel[]) || [] : [(value as DocumentEditModel) || ({} as DocumentEditModel)];
  logger.debug(`${name}: getFiles: `, multiple, files);
  return files;
}

export const HookFormDocuments = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: HookFormDocumentsProps<TFieldValues, TName>
) => {
  const { label, name, control, required, multiple = false, accept } = props;
  const enumLabels = useEnumLabels();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field, fieldState }) => {
        const { error } = fieldState;
        const { onChange, value } = field;

        const handleOnChange = (filesToSet: DocumentEditModel[]) => {
          if (multiple) {
            onChange(filesToSet);
          } else {
            onChange(filesToSet.length > 0 ? filesToSet[filesToSet.length - 1] : undefined);
          }
        };

        let files = getFiles(multiple, value);

        const handleFileChange = async (event: CustomEvent<File[]>) => {
          // Always use the current value from form state
          let files = Array.isArray(value) ? [...value] : [];
          const newFiles = event.detail;
          const numFiles = newFiles.length;

          // Remove any new (not-yet-uploaded) files from form state that are not present in the new dropzone file list
          files = files.filter((f) => {
            if (f.id) return true; // keep existing files
            // For new files, keep only if still present in dropzone
            return newFiles.some((nf) => nf.name === f.name);
          });

          // Add or update files from dropzone
          for (const newFile of newFiles) {
            const newName = newFile.name;
            let file = files.find((x) => x.name == newName);
            if (!file) {
              file = {
                name: newName,
                extension: "",
                status: FileActionEnum.Upload,
                file: newFile,
              };
              files.push(file);
            } else if (file.id) {
              file.status = FileActionEnum.Overwrite;
              file.file = newFile;
            }
          }

          // If dropzone is empty, revert all existing files to Exists
          if (numFiles === 0) {
            files = files.filter((x) => x.id).map((x) => ({ ...x, status: FileActionEnum.Exists }));
          }

          handleOnChange([...files]);
        };

        const handleRevertDocument = (x: DocumentEditModel, filesToSet: DocumentEditModel[]) => {
          if (x.id) {
            // revert existing file -> reset flag.
            logger.debug(`${name}: handleRevertDocument(): change status from '${x.status}' to 'Exists'`, x);
            x.status = FileActionEnum.Exists;
          } else {
            // revert new file to be uploaded -> remove from list.
            logger.debug(`${name}: handleRevertDocument(): remove (new) ${x.status} from files list`, x);
            filesToSet = filesToSet.filter((y) => y !== x);
          }

          return filesToSet;
        };

        const handleDeleteDocument = (x: DocumentEditModel, filesToSet: DocumentEditModel[]) => {
          if (x.id) {
            // delete existing file -> reset overwrit flag.
            logger.debug(`${name}: handleDeleteDocument(): change status from '${x.status}' to 'Delete'`, x);
            x.status = FileActionEnum.Delete;
          } else {
            // delete new file to be uploaded -> remove from list.
            logger.debug(`${name}: handleDeleteDocument(): remove (new) ${x.status} from files list`, x);
            filesToSet = filesToSet.filter((y) => y !== x);
          }

          return filesToSet;
        };

        return (
          <SbbFormField size="l" data-name={name} className="hook-form-documents">
            <div className="d-flex flex-column w-100">
              <LeanLabel name={name} title={label} required={required} />

              <SbbFileSelectorDropzone
                multiple={multiple}
                multipleMode="persistent"
                className="pb-3 pt-3 w-100"
                titleContent={label}
                onFilechanged={handleFileChange}
                size="s"
                accept={accept}
              />

              {files.length > 0 && <div className="existing-documents-top" />}

              {files.map((f: DocumentEditModel) => {
                const status = f.status;
                if (f.status === FileActionEnum.Upload) return null;

                const fileName = f.name;
                const key = `${f.id}-${fileName}`;
                const canRevert = status !== FileActionEnum.Exists;
                const canDelete = !!f.id && status !== FileActionEnum.Delete;

                const className = classNames(
                  "existingDocumentName",
                  "global-tooltip-trigger",
                  status == FileActionEnum.Delete ? "to-delete" : ""
                );

                return (
                  <div className="existingDocumentItem" key={key}>
                    <a className={className} title={fileName}>
                      {getTruncatedText(fileName, 40)}
                    </a>

                    <span className="existingDocumentStatus">{enumLabels.getFileAction(status)}</span>
                    <span className="existingDocumentActionButton">
                      <>
                        {canRevert && (
                          <SbbSecondaryButton
                            onClick={() => {
                              const filesToSet = handleRevertDocument(f, files);
                              handleOnChange([...filesToSet]);
                            }}
                            iconName="arrow-circle-reset-small"
                            size="s"
                          />
                        )}
                        {canDelete && (
                          <SbbSecondaryButton
                            iconName="trash-small"
                            size="s"
                            onClick={() => {
                              const filesToSet = handleDeleteDocument(f, files);
                              handleOnChange([...filesToSet]);
                            }}
                          />
                        )}
                      </>
                    </span>
                  </div>
                );
              })}

              {files.length > 0 && <div className="existing-documents-bottom" />}
              <HookFormError error={error} />
            </div>
          </SbbFormField>
        );
      }}
    />
  );
};
